import { createAjv } from "@jsonforms/core";
import { KeywordDefinition } from "ajv";
import AjvErrors from "ajv-errors";

import {
  checkResidencesMeetRequirementsForCountry,
  checkResidencesMeetRequirementsForPeriod,
  checkResidencesMeetRequirementsForUkSubdivision,
  Residence,
} from "./address-utils";
import {
  NO_UNDECLARED_RESIDENCE_OVERLAPS_KEYWORD,
  residencesHaveUndeclaredOverlap,
} from "./residence-overlap";

export const ajv = createAjv({
  useDefaults: true,
  allErrors: true,
  multipleOfPrecision: 5,
});

// Needs to stay synced with certn-backend
const INTERNATIONAL_CRIMINAL_CHECK_COUNTRY_CODES = [
  "AD",
  "AE",
  "AF",
  "AG",
  "AL",
  "AM",
  "AO",
  "AR",
  "AT",
  "AU",
  "AZ",
  "BA",
  "BB",
  "BD",
  "BE",
  "BF",
  "BG",
  "BH",
  "BI",
  "BJ",
  "BM",
  "BN",
  "BO",
  "BR",
  "BS",
  "BW",
  "BY",
  "BZ",
  "CD",
  "CF",
  "CH",
  "CI",
  "CL",
  "CM",
  "CN",
  "CO",
  "CR",
  "CU",
  "CV",
  "CY",
  "CZ",
  "DE",
  "DK",
  "DM",
  "DO",
  "DZ",
  "EC",
  "EE",
  "EG",
  "ES",
  "ET",
  "FI",
  "FJ",
  "FK",
  "FO",
  "FR",
  "GA",
  "GD",
  "GE",
  "GH",
  "GI",
  "GL",
  "GM",
  "GN",
  "GR",
  "GT",
  "HK",
  "HN",
  "HR",
  "HT",
  "HU",
  "ID",
  "IE",
  "IL",
  "IN",
  "IQ",
  "IR",
  "IS",
  "IT",
  "JM",
  "JO",
  "JP",
  "KE",
  "KG",
  "KH",
  "KN",
  "KR",
  "KW",
  "KY",
  "KZ",
  "LB",
  "LC",
  "LI",
  "LK",
  "LR",
  "LS",
  "LT",
  "LU",
  "LV",
  "LY",
  "MA",
  "MC",
  "MD",
  "ME",
  "MG",
  "MK",
  "ML",
  "MM",
  "MN",
  "MO",
  "MT",
  "MU",
  "MW",
  "MX",
  "MY",
  "MZ",
  "NA",
  "NE",
  "NG",
  "NI",
  "NL",
  "NO",
  "NP",
  "NZ",
  "OM",
  "PA",
  "PE",
  "PG",
  "PH",
  "PK",
  "PL",
  "PS",
  "PT",
  "PY",
  "QA",
  "RO",
  "RS",
  "RU",
  "RW",
  "SA",
  "SD",
  "SE",
  "SG",
  "SI",
  "SK",
  "SL",
  "SN",
  "SO",
  "SR",
  "SV",
  "SY",
  "SZ",
  "TC",
  "TG",
  "TH",
  "TJ",
  "TM",
  "TN",
  "TR",
  "TT",
  "TW",
  "TZ",
  "UA",
  "UG",
  "UY",
  "UZ",
  "VE",
  "VN",
  "ZA",
  "ZM",
  "ZW",
] as const;

const getHasResidenceInCountryConstraint = (
  countryCode: string
): KeywordDefinition => ({
  keyword: `has_residence_in_${countryCode}`,
  schemaType: "object",
  validate: (
    when: {
      months?: number;
      days?: number;
    },
    residences: Record<string, unknown>[] | undefined | null
  ) => {
    if (!when.months && !when.days) {
      return false;
    }
    if (!residences) {
      return false;
    }

    return checkResidencesMeetRequirementsForCountry(
      residences as Residence[],
      when,
      countryCode
    );
  },
});

const UK_SUBDIVISION_CODES = ["NIR", "SCT", "ENG", "WLS"];

const getHasResidenceInUkSubdivisionConstraint = (
  subdivisionCode: string
): KeywordDefinition => ({
  keyword: `has_residence_in_${subdivisionCode}`,
  schemaType: "object",
  validate: (
    when: {
      months?: number;
      days?: number;
    },
    residences: Record<string, unknown>[] | undefined | null
  ) => {
    if (!when.months && !when.days) {
      return false;
    }
    if (!residences) {
      return false;
    }

    return checkResidencesMeetRequirementsForUkSubdivision(
      residences as Residence[],
      when,
      subdivisionCode
    );
  },
});

const getPostalCodeForCountryConstraint = (
  countryCode: string
): KeywordDefinition => ({
  keyword: `postalCodeFor${countryCode}Residences`,
  schemaType: "string",
  validate: (
    regexPattern: string,
    residences: Record<string, unknown>[] | undefined | null
  ) => {
    if (!residences || residences.length === 0) {
      return false;
    }

    const countryResidences = (residences as Residence[]).filter(
      (residence) => residence.address.country === countryCode
    );

    if (countryResidences.length === 0) {
      return false;
    }

    return countryResidences.every((residence) => {
      return (
        typeof residence.address.postal_code === "string" &&
        residence.address.postal_code.match(new RegExp(regexPattern)) !== null
      );
    });
  },
});

type MustHaveDocument = { document_type: string; education_idx: number };

export const keywords: KeywordDefinition[] = [
  {
    keyword: "allowFutureDate",
    schemaType: "boolean",
    validate: (allowFutureDate: boolean, data: unknown) => {
      if (allowFutureDate) {
        return true;
      }

      return typeof data === "string" && new Date(data) <= new Date();
    },
  },
  {
    keyword: "allowPastDate",
    schemaType: "boolean",
    validate: (allowPastDate: boolean, data: unknown) => {
      if (allowPastDate) {
        return true;
      }

      return typeof data === "string" && new Date(data) >= new Date();
    },
  },
  {
    keyword: "regexPattern",
    schemaType: "string",
    validate: (regexPattern: string, data: unknown) => {
      if (!regexPattern) {
        return true;
      }

      return (
        typeof data === "string" &&
        data.match(new RegExp(regexPattern)) !== null
      );
    },
  },
  {
    keyword: "minMonths",
    schemaType: "number",
    validate: (
      minMonths: number,
      addresses: Record<string, unknown>[] | undefined | null
    ) => {
      if (!minMonths) {
        return true;
      }
      if (!addresses) {
        return false;
      }

      return checkResidencesMeetRequirementsForPeriod(
        // Asserting the type so the function knows that each address will have a start & end date key
        addresses as Residence[],
        { type: "months", value: minMonths }
      );
    },
  },
  {
    keyword: "nzCurrentRegionMinMonths",
    schemaType: "number",
    validate: (
      minMonths: number,
      residences: Record<string, unknown>[] | undefined | null
    ) => {
      if (!minMonths || !residences) {
        return true;
      }

      const hasNzCurrent = (residences as Residence[]).some(
        (residence) =>
          !residence.end_date &&
          residence.address?.country?.toUpperCase() === "NZ"
      );

      if (!hasNzCurrent) {
        return true;
      }

      return checkResidencesMeetRequirementsForPeriod(
        residences as Residence[],
        { type: "months", value: minMonths }
      );
    },
  },
  {
    keyword: "minDays",
    schemaType: "number",
    validate: (
      minDays: number,
      addresses: Record<string, unknown>[] | undefined | null
    ) => {
      if (!minDays) {
        return true;
      }
      if (!addresses) {
        return false;
      }

      return checkResidencesMeetRequirementsForPeriod(
        // Asserting the type so the function knows that each address will have a start & end date key
        addresses as Residence[],
        { type: "days", value: minDays }
      );
    },
  },
  getPostalCodeForCountryConstraint("GB"),
  getHasResidenceInCountryConstraint("US"),
  getHasResidenceInCountryConstraint("GB"),
  getHasResidenceInCountryConstraint("CA"),
  ...INTERNATIONAL_CRIMINAL_CHECK_COUNTRY_CODES.map((code) =>
    getHasResidenceInCountryConstraint(code)
  ),
  ...UK_SUBDIVISION_CODES.map((code) =>
    getHasResidenceInUkSubdivisionConstraint(code)
  ),
  {
    keyword: NO_UNDECLARED_RESIDENCE_OVERLAPS_KEYWORD,
    schemaType: "boolean",
    validate: (
      enabled: boolean,
      residences: Record<string, unknown>[] | undefined | null
    ) => {
      if (!enabled) {
        return true;
      }
      return !residencesHaveUndeclaredOverlap(residences as Residence[]);
    },
  },
  {
    keyword: "minEmployerYears",
    schemaType: "number",
    validate: (
      minYears: number,
      employers: Record<string, unknown>[] | undefined | null
    ) => {
      return false;
    },
  },
].concat(
  [...Array(255).keys()].map((key) => ({
    keyword: `mustHaveDocument${key}`,
    schemaType: "object",
    validate: (
      mustHaveDocument: MustHaveDocument,
      educations: MustHaveDocument[]
    ) =>
      educations.some(
        (education) =>
          education.document_type === mustHaveDocument.document_type &&
          Number(education.education_idx) === mustHaveDocument.education_idx
      ),
  }))
) as KeywordDefinition[];

AjvErrors(ajv, { singleError: true });
keywords.forEach((keyword) => ajv.addKeyword(keyword));
